import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { apiUrl } from "../config/api";

type UserRole = "POWER_ADMIN" | "ADMIN" | "MANAGER" | "STAFF" | "VIEWER";

type SaleChannelInfo = {
  id: number;
  code: string;
  label: string;
  is_active: boolean;
};

type UserItem = {
  id: number;
  username: string;
  full_name: string;
  role: UserRole;
  is_active: boolean;
  sale_channels: SaleChannelInfo[];
};

type UsersResponse = {
  ok?: boolean;
  items?: unknown;
  error?: string;
};

const USERS_API = apiUrl("/users");
const SALE_CHANNELS_API = apiUrl("/sale-channels");
const TOKEN_STORAGE_KEY = "bg_shop_token";
const ROLE_OPTIONS: UserRole[] = ["POWER_ADMIN", "ADMIN", "MANAGER", "STAFF", "VIEWER"];

function toSaleChannelInfo(row: unknown): SaleChannelInfo | null {
  if (!row || typeof row !== "object") return null;
  const data = row as Record<string, unknown>;
  const id = Number(data.id);
  if (!Number.isInteger(id) || id <= 0) return null;
  if (typeof data.code !== "string" || !data.code.trim()) return null;
  if (typeof data.label !== "string" || !data.label.trim()) return null;

  return {
    id,
    code: data.code.trim(),
    label: data.label.trim(),
    is_active: Boolean(data.is_active),
  };
}

function toUserItem(row: unknown): UserItem | null {
  if (!row || typeof row !== "object") return null;
  const data = row as Record<string, unknown>;
  const id = Number(data.id);
  if (!Number.isInteger(id) || id <= 0) return null;
  if (typeof data.username !== "string" || !data.username.trim()) return null;
  if (typeof data.full_name !== "string") return null;
  if (typeof data.role !== "string") return null;

  return {
    id,
    username: data.username.trim(),
    full_name: data.full_name,
    role: data.role as UserRole,
    is_active: Boolean(data.is_active),
    sale_channels: Array.isArray(data.sale_channels)
      ? data.sale_channels
          .map(toSaleChannelInfo)
          .filter((item): item is SaleChannelInfo => item != null)
      : [],
  };
}

function getErrorMessage(code: string): string {
  switch (code) {
    case "forbidden":
      return "This account does not have permission to view users.";
    case "failed_to_update_user_status":
      return "Unable to update the user status right now.";
    case "failed_to_update_user_role":
      return "Unable to update the user role right now.";
    case "failed_to_update_user_sale_channels":
      return "Unable to update the user's sale channel access right now.";
    case "sale_channel_ids_required":
      return "Please select at least one sale channel.";
    case "invalid_sale_channel_ids":
      return "One or more selected sale channels are invalid.";
    case "user_not_found":
      return "The selected user was not found.";
    case "cannot_change_power_admin_role":
      return "POWER_ADMIN role cannot be changed.";
    case "missing_auth_token":
    case "invalid_auth_token":
      return "Please log in again before viewing users.";
    default:
      return "Unable to load the user list right now.";
  }
}

export default function UserListPage(): JSX.Element {
  const [items, setItems] = useState<UserItem[]>([]);
  const [saleChannels, setSaleChannels] = useState<SaleChannelInfo[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [updatingId, setUpdatingId] = useState<number | null>(null);
  const [roleDrafts, setRoleDrafts] = useState<Record<number, UserRole>>({});
  const [saleChannelDrafts, setSaleChannelDrafts] = useState<Record<number, number[]>>({});
  const [expandedBranchPickers, setExpandedBranchPickers] = useState<Record<number, boolean>>({});

  useEffect(() => {
    void loadUsersAndSaleChannels();
  }, []);

  // PMN-CRITICAL(/UserListPage): Users and sale channel master load together; drafts drive role and branch permission writes.
  async function loadUsersAndSaleChannels(): Promise<void> {
    const token = localStorage.getItem(TOKEN_STORAGE_KEY) ?? "";
    if (!token) {
      setError("Please log in again before viewing users.");
      setLoading(false);
      return;
    }

    setLoading(true);

    try {
      const [usersResp, saleChannelsResp] = await Promise.all([
        fetch(USERS_API, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }),
        fetch(SALE_CHANNELS_API, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }),
      ]);

      const usersData = (await usersResp.json().catch(() => ({}))) as UsersResponse;
      const saleChannelsData = (await saleChannelsResp.json().catch(() => ({}))) as UsersResponse;

      if (!usersResp.ok || !usersData.ok || !Array.isArray(usersData.items)) {
        throw new Error(usersData.error ?? "failed_to_fetch_users");
      }
      if (!saleChannelsResp.ok || !saleChannelsData.ok || !Array.isArray(saleChannelsData.items)) {
        throw new Error(saleChannelsData.error ?? "failed_to_fetch_sale_channels");
      }

      const nextItems = usersData.items
        .map(toUserItem)
        .filter((item): item is UserItem => item != null);
      const nextSaleChannels = saleChannelsData.items
        .map(toSaleChannelInfo)
        .filter((item): item is SaleChannelInfo => item != null);

      setItems(nextItems);
      setSaleChannels(nextSaleChannels);
      setRoleDrafts(
        nextItems.reduce<Record<number, UserRole>>((acc, item) => {
          acc[item.id] = item.role;
          return acc;
        }, {})
      );
      setSaleChannelDrafts(
        nextItems.reduce<Record<number, number[]>>((acc, item) => {
          acc[item.id] = item.sale_channels.map((channel) => channel.id);
          return acc;
        }, {})
      );
      setError("");
    } catch (err) {
      const code = err instanceof Error ? err.message : "failed_to_fetch_users";
      setError(getErrorMessage(code));
    } finally {
      setLoading(false);
    }
  }

  // PMN-CRITICAL(/UserListPage): Status changes users.is_active and can block future login for this account.
  async function toggleUserStatus(item: UserItem): Promise<void> {
    const token = localStorage.getItem(TOKEN_STORAGE_KEY) ?? "";
    if (!token) {
      setError("Please log in again before viewing users.");
      return;
    }

    setUpdatingId(item.id);
    setError("");

    try {
      const resp = await fetch(apiUrl(`/users/${item.id}/status`), {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          is_active: !item.is_active,
        }),
      });
      const data = (await resp.json().catch(() => ({}))) as {
        ok?: boolean;
        error?: string;
        is_active?: boolean;
      };

      if (!resp.ok || !data.ok) {
        throw new Error(data.error ?? "failed_to_update_user_status");
      }

      setItems((prev) =>
        prev.map((row) =>
          row.id === item.id
            ? {
                ...row,
                is_active: data.is_active === true,
              }
            : row
        )
      );
    } catch (err) {
      const code = err instanceof Error ? err.message : "failed_to_update_user_status";
      setError(getErrorMessage(code));
    } finally {
      setUpdatingId(null);
    }
  }

  // PMN-CRITICAL(/UserListPage): POWER_ADMIN role is locked by UI and backend; role changes affect route permissions.
  async function updateUserRole(item: UserItem): Promise<void> {
    const token = localStorage.getItem(TOKEN_STORAGE_KEY) ?? "";
    if (!token) {
      setError("Please log in again before viewing users.");
      return;
    }

    const nextRole = roleDrafts[item.id] ?? item.role;
    if (nextRole === item.role) {
      return;
    }

    setUpdatingId(item.id);
    setError("");

    try {
      const resp = await fetch(apiUrl(`/users/${item.id}/role`), {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          role: nextRole,
        }),
      });
      const data = (await resp.json().catch(() => ({}))) as {
        ok?: boolean;
        error?: string;
        role?: UserRole;
      };

      if (!resp.ok || !data.ok || !data.role) {
        throw new Error(data.error ?? "failed_to_update_user_role");
      }

      setItems((prev) =>
        prev.map((row) =>
          row.id === item.id
            ? {
                ...row,
                role: data.role as UserRole,
              }
            : row
        )
      );
      setRoleDrafts((prev) => ({
        ...prev,
        [item.id]: data.role as UserRole,
      }));
    } catch (err) {
      const code = err instanceof Error ? err.message : "failed_to_update_user_role";
      setError(getErrorMessage(code));
    } finally {
      setUpdatingId(null);
    }
  }

  // PMN-CRITICAL(/UserListPage): Saving branches replaces all user_sale_channels rows for this user; missing ids are removed.
  async function updateUserSaleChannels(item: UserItem): Promise<void> {
    const token = localStorage.getItem(TOKEN_STORAGE_KEY) ?? "";
    if (!token) {
      setError("Please log in again before viewing users.");
      return;
    }

    const nextIds = Array.from(new Set(saleChannelDrafts[item.id] ?? []));
    const currentIds = item.sale_channels.map((channel) => channel.id).sort((a, b) => a - b);
    const draftIds = [...nextIds].sort((a, b) => a - b);

    if (draftIds.length === currentIds.length && draftIds.every((id, index) => id === currentIds[index])) {
      return;
    }

    setUpdatingId(item.id);
    setError("");

    try {
      const resp = await fetch(apiUrl(`/users/${item.id}/sale-channels`), {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          sale_channel_ids: nextIds,
        }),
      });
      const data = (await resp.json().catch(() => ({}))) as {
        ok?: boolean;
        error?: string;
        sale_channels?: unknown;
      };

      if (!resp.ok || !data.ok || !Array.isArray(data.sale_channels)) {
        throw new Error(data.error ?? "failed_to_update_user_sale_channels");
      }

      const nextSaleChannels = data.sale_channels
        .map(toSaleChannelInfo)
        .filter((channel): channel is SaleChannelInfo => channel != null);

      setItems((prev) =>
        prev.map((row) =>
          row.id === item.id
            ? {
                ...row,
                sale_channels: nextSaleChannels,
              }
            : row
        )
      );
      setSaleChannelDrafts((prev) => ({
        ...prev,
        [item.id]: nextSaleChannels.map((channel) => channel.id),
      }));
    } catch (err) {
      const code = err instanceof Error ? err.message : "failed_to_update_user_sale_channels";
      setError(getErrorMessage(code));
    } finally {
      setUpdatingId(null);
    }
  }

  return (
    <div className="min-vh-100 bg-primary py-4 py-md-5">
      <div className="container">
        <div className="row justify-content-center">
          <div className="col-12 col-xl-10">
            <div className="card border-0 shadow-sm rounded-4">
              <div className="card-body p-4 p-md-5">
                <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-3 mb-4">
                  <div>
                    <h2 className="text-primary mb-1">User List</h2>
                    <div className="text-muted">
                      See which accounts exist and what role each account has.
                    </div>
                  </div>
                  <div className="d-flex gap-2">
                    <Link to="/CreateUserPage" className="btn btn-primary">
                      Create User
                    </Link>
                    <Link to="/" className="btn btn-outline-primary">
                      Back to Menu
                    </Link>
                  </div>
                </div>

                {error && <div className="alert alert-danger rounded-3">{error}</div>}

                <div className="table-responsive">
                  <table className="table align-middle">
                    <thead>
                      <tr>
                        <th>Username</th>
                        <th>Full Name</th>
                        <th>Role</th>
                        <th style={{ minWidth: 280 }}>Sale Channels</th>
                        <th>Status</th>
                        <th className="text-end">Manage</th>
                      </tr>
                    </thead>
                    <tbody>
                      {loading ? (
                        <tr>
                          <td colSpan={6} className="text-center text-muted py-4">
                            Loading users...
                          </td>
                        </tr>
                      ) : items.length === 0 ? (
                        <tr>
                          <td colSpan={6} className="text-center text-muted py-4">
                            No users found.
                          </td>
                        </tr>
                      ) : (
                        items.map((item) => (
                          <tr key={item.id}>
                            <td className="fw-semibold">{item.username}</td>
                            <td>{item.full_name || "-"}</td>
                            <td style={{ minWidth: 220 }}>
                              <div className="d-flex gap-2 align-items-center">
                                <select
                                  className="form-select form-select-sm"
                                  value={roleDrafts[item.id] ?? item.role}
                                  disabled={updatingId === item.id || item.role === "POWER_ADMIN"}
                                  onChange={(e) =>
                                    setRoleDrafts((prev) => ({
                                      ...prev,
                                      [item.id]: e.target.value as UserRole,
                                    }))
                                  }
                                >
                                  {ROLE_OPTIONS.map((role) => (
                                    <option key={role} value={role}>
                                      {role}
                                    </option>
                                  ))}
                                </select>
                                <button
                                  type="button"
                                  className="btn btn-sm btn-outline-primary"
                                  disabled={
                                    updatingId === item.id ||
                                    item.role === "POWER_ADMIN" ||
                                    (roleDrafts[item.id] ?? item.role) === item.role
                                  }
                                  onClick={() => void updateUserRole(item)}
                                >
                                  Save
                                </button>
                              </div>
                              {item.role === "POWER_ADMIN" && (
                                <div className="small text-muted mt-1">
                                  POWER_ADMIN role is locked.
                                </div>
                              )}
                            </td>
                            <td style={{ minWidth: 280 }}>
                              {(() => {
                                const activeChannelIds = saleChannels
                                  .filter((channel) => channel.is_active)
                                  .map((channel) => channel.id);
                                const selectedIds = saleChannelDrafts[item.id] ?? [];
                                const isExpanded = expandedBranchPickers[item.id] === true;
                                const visibleChannels = isExpanded ? saleChannels : saleChannels.slice(0, 3);
                                const hasAllActiveBranches =
                                  activeChannelIds.length > 0 &&
                                  activeChannelIds.every((id) => selectedIds.includes(id));

                                return (
                              <div className="border rounded-3 p-2 bg-light-subtle">
                                <div className="form-check mb-2 pb-2 border-bottom">
                                  <input
                                    id={`all-branches-${item.id}`}
                                    type="checkbox"
                                    className="form-check-input"
                                    checked={hasAllActiveBranches}
                                    disabled={updatingId === item.id || activeChannelIds.length === 0}
                                    onChange={(e) =>
                                      setSaleChannelDrafts((prev) => ({
                                        ...prev,
                                        [item.id]: e.target.checked ? activeChannelIds : [],
                                      }))
                                    }
                                  />
                                  <label className="form-check-label fw-semibold" htmlFor={`all-branches-${item.id}`}>
                                    All branches
                                  </label>
                                </div>
                                <div className="d-grid gap-2">
                                  {visibleChannels.map((channel) => {
                                    const checked = (saleChannelDrafts[item.id] ?? []).includes(channel.id);
                                    return (
                                      <label
                                        key={channel.id}
                                        className={`form-check d-flex align-items-center gap-2 mb-0 ${
                                          channel.is_active ? "" : "text-muted"
                                        }`}
                                      >
                                        <input
                                          type="checkbox"
                                          className="form-check-input mt-0"
                                          checked={checked}
                                          disabled={updatingId === item.id || !channel.is_active}
                                          onChange={(e) =>
                                            setSaleChannelDrafts((prev) => {
                                              const current = new Set(prev[item.id] ?? []);
                                              if (e.target.checked) {
                                                current.add(channel.id);
                                              } else {
                                                current.delete(channel.id);
                                              }
                                              return {
                                                ...prev,
                                                [item.id]: Array.from(current),
                                              };
                                            })
                                          }
                                        />
                                        <span className="small">
                                          {channel.label}{" "}
                                          <span className="text-muted">({channel.code})</span>
                                          {!channel.is_active && " - Inactive"}
                                        </span>
                                      </label>
                                    );
                                  })}
                                </div>
                                {saleChannels.length > 3 && (
                                  <div className="mt-2">
                                    <button
                                      type="button"
                                      className="btn btn-link btn-sm px-0 text-decoration-none"
                                      onClick={() =>
                                        setExpandedBranchPickers((prev) => ({
                                          ...prev,
                                          [item.id]: !isExpanded,
                                        }))
                                      }
                                    >
                                      {isExpanded ? "Show less" : `See all (${saleChannels.length})`}
                                    </button>
                                  </div>
                                )}
                                <div className="d-flex justify-content-between align-items-center mt-2 pt-2 border-top">
                                  <div className="small text-muted">
                                    {(saleChannelDrafts[item.id] ?? []).length} selected
                                  </div>
                                  <button
                                    type="button"
                                    className="btn btn-sm btn-outline-primary"
                                    disabled={updatingId === item.id}
                                    onClick={() => void updateUserSaleChannels(item)}
                                  >
                                    Save Branches
                                  </button>
                                </div>
                              </div>
                                );
                              })()}
                            </td>
                            <td>
                              <span
                                className={`badge ${
                                  item.is_active ? "text-bg-success" : "text-bg-secondary"
                                }`}
                              >
                                {item.is_active ? "Active" : "Inactive"}
                              </span>
                            </td>
                            <td className="text-end">
                              <button
                                type="button"
                                className={`btn btn-sm ${
                                  item.is_active ? "btn-outline-secondary" : "btn-outline-success"
                                }`}
                                disabled={updatingId === item.id}
                                onClick={() => void toggleUserStatus(item)}
                              >
                                {updatingId === item.id
                                  ? "Updating..."
                                  : item.is_active
                                  ? "Set Inactive"
                                  : "Set Active"}
                              </button>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
